import { envelopeData } from '@/lib/envelope'
import { useState } from 'react'
import { createFileRoute } from '@tanstack/react-router'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import {
  User,
  Mail,
  Shield,
  Flame,
  Trophy,
  LogOut,
  Lock,
  Pencil,
  Save,
  X,
  CreditCard,
  Receipt,
  RefreshCw,
} from 'lucide-react'
import { toast } from 'sonner'
import { profileQueryOptions, quickStatsQueryOptions, useUpdateCustomerProfile, useUpdateAuthorProfile } from '@/lib/queries/dashboard'
import { dashboardKeys } from '@/lib/queries/keys'
import { OrdersHistory } from '@/components/billing/orders-history'
import { SubscriptionManager } from '@/components/billing/subscription-manager'
import { PaymentMethodSelector } from '@/components/billing/payment-method-selector'
import { authClient, signOut } from '@/lib/auth/client'
import { getTier } from '@/lib/xp/tiers'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'

// ============================================================================
// Helpers
// ============================================================================

function toStr(v: unknown): string {
  if (v == null) return ''
  if (v instanceof Date) return v.toISOString().slice(0, 10)
  return String(v)
}

export const Route = createFileRoute('/_app/minha-conta')({
  loader: async ({ context }) => {
    const [profile, stats] = await Promise.allSettled([
      context.queryClient.prefetchQuery(profileQueryOptions()),
      context.queryClient.prefetchQuery(quickStatsQueryOptions()),
    ])
    return {
      profile: profile.status === 'fulfilled' ? profile.value : null,
      stats: stats.status === 'fulfilled' ? stats.value : null,
    }
  },
  component: AccountPage,
})

// ============================================================================
// Main page
// ============================================================================

function AccountPage() {
  // o loader usa `prefetchQuery`, que devolve void e já deixa o cache quente —
  // passar esse void como `initialData` só apagava o dado vindo do SSR.
  const { data: profileData } = useQuery(profileQueryOptions())
  const { data: statsData } = useQuery(quickStatsQueryOptions())

  const profile = envelopeData(profileData)
  const stats = envelopeData(statsData)

  return (
    <div className="mx-auto max-w-2xl space-y-6 pb-12">
      <div className="flex items-center gap-3">
        <User className="h-6 w-6 text-primary" />
        <h1 className="text-2xl font-bold text-foreground">Minha conta</h1>
      </div>

      {/* XP + Elo */}
      {stats && <XpTierSection stats={stats} />}

      {/* Dados pessoais */}
      {profile && <PersonalDataSection profile={profile} />}

      {/* Perfil comunidade */}
      {profile && <AuthorProfileSection author={profile.author} />}

      {/* Assinaturas */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <RefreshCw className="h-4 w-4 text-primary" />
          Minhas assinaturas
        </h3>
        <SubscriptionManager />
      </div>

      {/* Histórico de compras */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <Receipt className="h-4 w-4 text-primary" />
          Histórico de compras
        </h3>
        <OrdersHistory />
      </div>

      {/* Meus cartões */}
      <div className="rounded-xl border border-border bg-card p-6 space-y-4">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <CreditCard className="h-4 w-4 text-primary" />
          Meus cartões
        </h3>
        <CardManagementSection />
      </div>

      {/* Alterar senha */}
      <ChangePasswordSection />

      {/* Sair da conta */}
      <div className="rounded-xl border border-border bg-card p-6">
        <Button
          variant="outline"
          className="w-full border-destructive text-destructive hover:bg-destructive/10"
          onClick={() =>
            signOut({
              fetchOptions: {
                onSuccess: () => {
                  window.location.href = '/login'
                },
                onError: (ctx) => {
                  console.error('[signOut] failed:', ctx.error)
                },
              },
            })
          }
        >
          <LogOut className="mr-2 h-4 w-4" />
          Sair da conta
        </Button>
      </div>
    </div>
  )
}

// ============================================================================
// XP + Elo section (read-only)
// ============================================================================

function XpTierSection({ stats }: { stats: any }) {
  const tier = getTier(stats.currentLevel)
  const progressPercent = Math.max(0, Math.min(100, stats.progressPercent ?? 0))

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
        <Shield className="h-4 w-4 text-primary" />
        Nível e Experiência
      </h3>

      {/* Tier + level */}
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
          <span className="text-lg font-bold text-primary">{stats.currentLevel}</span>
        </div>
        <div>
          <p className="text-sm font-semibold text-foreground">
            Nível {stats.currentLevel}{' '}
            <span className={tier.color}>— {tier.name}</span>
          </p>
          <p className="text-xs text-muted-foreground">
            {stats.totalXp} XP total · {stats.xpToNextLevel} XP para o próximo nível
          </p>
        </div>
      </div>

      {/* Progress bar */}
      <div>
        <div className="flex items-center justify-between text-xs text-muted-foreground mb-1">
          <span>Progresso</span>
          <span>{progressPercent}%</span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-accent">
          <div
            className="h-full rounded-full bg-primary transition-all duration-500"
            style={{ width: `${progressPercent}%` }}
          />
        </div>
      </div>

      {/* Extra stats */}
      <div className="flex items-center gap-6 text-sm text-muted-foreground">
        {stats.currentStreak > 0 && (
          <div className="flex items-center gap-1.5">
            <Flame className="h-4 w-4 text-orange-500" />
            <span>{stats.currentStreak} dias de streak</span>
          </div>
        )}
        {stats.rankingPosition > 0 && (
          <div className="flex items-center gap-1.5">
            <Trophy className="h-4 w-4 text-yellow-500" />
            <span>#{stats.rankingPosition} de {stats.totalStudents}</span>
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Personal data section (editable form)
// ============================================================================

function PersonalDataSection({ profile }: { profile: any }) {
  const queryClient = useQueryClient()
  const [editing, setEditing] = useState(false)
  const [firstName, setFirstName] = useState(profile.firstName ?? '')
  const [lastName, setLastName] = useState(profile.lastName ?? '')
  const [phone, setPhone] = useState(profile.phone ?? '')
  const [birthDate, setBirthDate] = useState(toStr(profile.birthDate))
  const [cpfCnpj, setCpfCnpj] = useState(profile.cpfCnpj ?? '')

  const mutation = useUpdateCustomerProfile()

  function handleCancel() {
    setFirstName(profile.firstName ?? '')
    setLastName(profile.lastName ?? '')
    setPhone(profile.phone ?? '')
    setBirthDate(toStr(profile.birthDate))
    setCpfCnpj(profile.cpfCnpj ?? '')
    setEditing(false)
  }

  function handleSave() {
    const updates = { firstName, lastName, phone, birthDate, cpfCnpj }
    mutation.mutate(updates, {
      onSuccess: (res) => {
        if (res?.success) {
          toast.success('Dados pessoais atualizados.')
          queryClient.setQueryData(dashboardKeys.profile(), (old: any) => {
            if (!old?.data) return old
            return { ...old, data: { ...old.data, ...updates } }
          })
          setEditing(false)
        } else {
          toast.error(res?.message ?? 'Erro ao atualizar.')
        }
      },
      onError: () => toast.error('Erro ao atualizar dados pessoais.'),
    })
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <User className="h-4 w-4 text-primary" />
          Dados pessoais
        </h3>
        {!editing && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            Editar
          </Button>
        )}
      </div>

      {/* Avatar + email (always read-only) */}
      <div className="flex items-center gap-4">
        <div className="flex h-14 w-14 shrink-0 items-center justify-center overflow-hidden rounded-full bg-accent">
          {profile.image ? (
            <img src={profile.image} alt="" className="h-14 w-14 rounded-full object-cover" />
          ) : (
            <span className="text-lg font-semibold text-accent-foreground">{profile.initials}</span>
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-foreground">{profile.name}</p>
          <p className="text-xs text-muted-foreground flex items-center gap-1">
            <Mail className="h-3 w-3" />
            {profile.email}
          </p>
        </div>
      </div>

      {editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="firstName">Nome</Label>
              <Input id="firstName" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="lastName">Sobrenome</Label>
              <Input id="lastName" value={lastName} onChange={(e) => setLastName(e.target.value)} />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="phone">Telefone</Label>
              <Input id="phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="birthDate">Data de nascimento</Label>
              <Input id="birthDate" type="date" value={birthDate} onChange={(e) => setBirthDate(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="cpfCnpj">CPF/CNPJ</Label>
            <Input id="cpfCnpj" value={cpfCnpj} onChange={(e) => setCpfCnpj(e.target.value)} />
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button size="sm" onClick={handleSave} disabled={mutation.isPending}>
              <Save className="mr-1.5 h-3.5 w-3.5" />
              {mutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCancel} disabled={mutation.isPending}>
              <X className="mr-1.5 h-3.5 w-3.5" />
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <FieldDisplay label="Nome" value={profile.firstName} />
          <FieldDisplay label="Sobrenome" value={profile.lastName} />
          <FieldDisplay label="Telefone" value={profile.phone} />
          <FieldDisplay label="Data de nascimento" value={profile.birthDate} />
          <FieldDisplay label="CPF/CNPJ" value={profile.cpfCnpj} />
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Author / community profile section
// ============================================================================

function AuthorProfileSection({ author }: { author: any | null }) {
  const queryClient = useQueryClient()
  const hasAuthor = !!author
  const [editing, setEditing] = useState(false)
  const [name, setName] = useState(author?.name ?? '')
  const [jobTitle, setJobTitle] = useState(author?.jobTitle ?? '')
  const [bio, setBio] = useState(author?.bio ?? '')

  const mutation = useUpdateAuthorProfile()

  function handleCancel() {
    setName(author?.name ?? '')
    setJobTitle(author?.jobTitle ?? '')
    setBio(author?.bio ?? '')
    setEditing(false)
  }

  function handleSave() {
    const updates = { name, jobTitle, bio }
    mutation.mutate(updates, {
      onSuccess: (res) => {
        if (res?.success) {
          toast.success('Perfil de comunidade atualizado.')
          queryClient.setQueryData(dashboardKeys.profile(), (old: any) => {
            if (!old?.data?.author) return old
            return { ...old, data: { ...old.data, author: { ...old.data.author, ...updates } } }
          })
          setEditing(false)
        } else {
          toast.error(res?.message ?? 'Erro ao atualizar.')
        }
      },
      onError: () => toast.error('Erro ao atualizar perfil de comunidade.'),
    })
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
          <User className="h-4 w-4 text-primary" />
          Perfil da comunidade
        </h3>
        {hasAuthor && !editing && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            <Pencil className="mr-1.5 h-3.5 w-3.5" />
            Editar
          </Button>
        )}
      </div>

      {!hasAuthor ? (
        <p className="text-sm text-muted-foreground">
          Nenhum perfil de comunidade associado a esta conta.
        </p>
      ) : editing ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="authorName">Nome</Label>
              <Input id="authorName" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="jobTitle">Cargo</Label>
              <Input id="jobTitle" value={jobTitle} onChange={(e) => setJobTitle(e.target.value)} />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="bio">Bio</Label>
            <Textarea id="bio" value={bio} onChange={(e) => setBio(e.target.value)} rows={3} />
          </div>
          <div className="flex items-center gap-2 pt-2">
            <Button size="sm" onClick={handleSave} disabled={mutation.isPending}>
              <Save className="mr-1.5 h-3.5 w-3.5" />
              {mutation.isPending ? 'Salvando...' : 'Salvar'}
            </Button>
            <Button size="sm" variant="ghost" onClick={handleCancel} disabled={mutation.isPending}>
              <X className="mr-1.5 h-3.5 w-3.5" />
              Cancelar
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-2 text-sm">
          <FieldDisplay label="Nome" value={author.name} />
          <FieldDisplay label="Cargo" value={author.jobTitle} />
          <FieldDisplay label="Bio" value={author.bio} />
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Change password section
// ============================================================================

function ChangePasswordSection() {
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isPending, setIsPending] = useState(false)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()

    if (newPassword !== confirmPassword) {
      toast.error('As senhas não coincidem.')
      return
    }
    if (newPassword.length < 8) {
      toast.error('A nova senha deve ter pelo menos 8 caracteres.')
      return
    }

    setIsPending(true)
    try {
      const { error } = await authClient.changePassword({
        currentPassword,
        newPassword,
        revokeOtherSessions: false,
      })
      if (error) {
        toast.error(error.message ?? 'Erro ao alterar senha.')
      } else {
        toast.success('Senha alterada com sucesso.')
        setCurrentPassword('')
        setNewPassword('')
        setConfirmPassword('')
      }
    } catch {
      toast.error('Erro ao alterar senha.')
    } finally {
      setIsPending(false)
    }
  }

  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-4">
      <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
        <Lock className="h-4 w-4 text-primary" />
        Alterar senha
      </h3>

      <form onSubmit={handleSubmit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="currentPassword">Senha atual</Label>
          <Input
            id="currentPassword"
            type="password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="newPassword">Nova senha</Label>
            <Input
              id="newPassword"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="confirmPassword">Confirmar nova senha</Label>
            <Input
              id="confirmPassword"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength={8}
            />
          </div>
        </div>
        <div className="pt-2">
          <Button type="submit" size="sm" disabled={isPending}>
            <Lock className="mr-1.5 h-3.5 w-3.5" />
            {isPending ? 'Alterando...' : 'Alterar senha'}
          </Button>
        </div>
      </form>
    </div>
  )
}

// ============================================================================
// Card management section (manage mode — no checkout)
// ============================================================================

function CardManagementSection() {
  const [showSelector, setShowSelector] = useState(false)

  if (!showSelector) {
    return (
      <div className="space-y-2">
        <Button variant="outline" size="sm" onClick={() => setShowSelector(true)}>
          <CreditCard className="mr-1.5 h-3.5 w-3.5" />
          Gerenciar cartões
        </Button>
      </div>
    )
  }

  return (
    <PaymentMethodSelector
      onSelect={async () => {
        // In manage mode, just close the selector
        setShowSelector(false)
      }}
      onCancel={() => setShowSelector(false)}
    />
  )
}

function FieldDisplay({ label, value }: { label: string; value: unknown }) {
  const display = toStr(value)
  return (
    <div>
      <span className="text-muted-foreground">{label}</span>
      <p className="font-medium text-foreground">{display || '—'}</p>
    </div>
  )
}
